<script setup>
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Card, CardContent } from 'dashboard/components-next/polaris/ui/card';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';

// Rebuilt inside on the kit's SettingsSection (phase 9.4): one card per topic, the title and a short description
// on the left, the controls on the right (stacked below 1024px). Same props and slots as before. The card draws its own
// edge (`withBorder` is gone). A section with no title or description takes one column.
defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  hideContent: { type: Boolean, default: false },
  beta: { type: Boolean, default: false },
  // one column: the content takes the whole width (a table), the kit's `wide`
  wide: { type: Boolean, default: false },
});
const { t } = useI18n();
const id = useId();
</script>

<template>
  <section
    :aria-labelledby="title || $slots.title ? id : undefined"
    data-settings-section
  >
    <Card>
      <CardContent
        class="grid gap-6"
        :class="{
          'lg:grid-cols-2':
            !wide &&
            (title ||
              beta ||
              $slots.title ||
              description ||
              $slots.description),
        }"
      >
        <div
          v-if="
            title || beta || $slots.title || description || $slots.description
          "
          class="grid content-start gap-2.5"
        >
          <h2
            v-if="title || beta || $slots.title"
            :id="id"
            class="flex flex-wrap items-center gap-2 type-polaris-card-title"
          >
            <slot name="title">{{ title }}</slot>
            <span v-if="beta" v-tooltip.top="t('GENERAL.BETA_DESCRIPTION')">
              <Badge variant="outline">{{ t('GENERAL.BETA') }}</Badge>
            </span>
          </h2>
          <p
            v-if="description || $slots.description"
            class="max-w-[21.25rem] type-polaris-body text-polaris-muted-foreground"
          >
            <slot name="description">{{ description }}</slot>
          </p>
        </div>
        <div class="grid min-w-0 content-start gap-5">
          <div v-if="$slots.headerActions" class="flex items-center">
            <slot name="headerActions" />
          </div>
          <slot v-if="!hideContent" />
        </div>
      </CardContent>
    </Card>
  </section>
</template>
